import { IconButton, Menu, MenuItem } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  AssemblyGoverningValueDocument,
  useAssemblyGoverningValueLazyQuery,
  useDeletePointOfInterestMutation,
} from '@/__generated__/graphql';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import * as state from '@/components/Analysis/state';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import {
  BLISTER_TYPE,
  DELETING_LABEL,
  PIT_TYPE,
  CANCEL_LABEL,
  DELETE_LABEL,
  POI_DELETE_DIALOG_TITLE,
  POI_DELETE_DIALOG_CONTENT,
} from '@/constants';
import { extractErrorMessageFromUnknownError } from '@/utils/error';

type Props = {
  poiId: string;
  onDelete?: () => void;
};

export const SelectedPoiMenuButton = ({ poiId, onDelete }: Props) => {
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const setSelectedPointOfInterestId = useSetAtom(poiState.selectedPointOfInterestId);
  const setApplyFilters = useSetAtom(state.applyFilters);
  const { showSnackBar } = useSnackBarMessage({});
  const setAllPOI = useSetAtom(poiState.structurePois);
  const setGoverningValue = useSetAtom(state.governingValueByAssemblyId);

  const assemblyId = useAtomValue(state.selectedAssemblyId);

  const open = useMemo(() => Boolean(anchorElement), [anchorElement]);

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  }, []);

  const [isLoading, setIsLoading] = useState(false);

  const [deletePoi] = useDeletePointOfInterestMutation({
    variables: { input: { pointOfInterestId: poiId } },
    refetchQueries: [AssemblyGoverningValueDocument],
    onCompleted: async (response) => {
      if (!response?.deletePointOfInterest?.id) return;
      const poiType = response.deletePointOfInterest?.type;
      if (!poiType || !assemblyId) return;
      const governingTypePois = [BLISTER_TYPE, PIT_TYPE, 'DEFECT_CLASS'];
      if (!governingTypePois.includes(poiType)) return;

      try {
        const { data } = await getGoverningValue();
        const governingId = data?.assembly?.recommendation?.governingValue?.pointOfInterest?.id;
        if (governingId) {
          setGoverningValue((previous) => ({
            ...previous,
            [assemblyId]: { pointOfInterest: { id: governingId } },
          }));
        }
      } catch {
        showSnackBar('Failed to update governing poi', 'error');
      }
    },
  });

  const [getGoverningValue] = useAssemblyGoverningValueLazyQuery({
    variables: { assemblyId },
  });

  const handleClose = useCallback(() => {
    setAnchorElement(null);
  }, []);

  const handleDeleteClick = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: poiData } = await deletePoi();
      const deletedPoi = poiData?.deletePointOfInterest;
      setAllPOI((previous) => previous.filter((poi) => poi.id !== deletedPoi?.id));
      setApplyFilters(true);
      setSelectedPointOfInterestId(undefined);
      setIsUpdateDialogOpen(false);
      showSnackBar('POI deleted successfully', 'success');
      onDelete?.();
    } catch (error) {
      const errorMessage = extractErrorMessageFromUnknownError(error);
      showSnackBar(errorMessage, 'error');
    } finally {
      setIsLoading(false);
      handleClose();
    }
  }, [
    deletePoi,
    setAllPOI,
    setApplyFilters,
    showSnackBar,
    setSelectedPointOfInterestId,
    handleClose,
    onDelete,
  ]);

  return (
    <div>
      <ConfirmationDialog
        loading={{
          state: isLoading,
          text: DELETING_LABEL,
        }}
        cancelButtonText={CANCEL_LABEL}
        confirmButtonText={DELETE_LABEL}
        description={POI_DELETE_DIALOG_CONTENT}
        title={POI_DELETE_DIALOG_TITLE}
        handleClose={() => {}}
        isOpen={isUpdateDialogOpen}
        onConfirm={handleDeleteClick}
        onCancel={() => setIsUpdateDialogOpen(false)}
      />
      <IconButton
        id="selected-poi-menu-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu id="basic-menu" anchorEl={anchorElement} open={open} onClose={handleClose}>
        <MenuItem disabled>
          <ShareIcon sx={{ mr: 1 }} />
          Share
        </MenuItem>
        <MenuItem
          onClick={() => {
            setIsUpdateDialogOpen(true);
          }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </div>
  );
};
